import mori from 'mori';
import {
  kffIsNumber,
  numberPrecision,
  getPrecision,
} from '../util/util';
import { appDefault } from '../config/config';

export function processCellCallback(columnDefs) {
  const defs = mori.toJs(columnDefs).reduce((prev, curr) => {
    prev[curr.headerGroup ?
      `${curr.headerGroup}__${curr.characteristic}` :
      curr.characteristic] = curr;
    return prev;
  }, {});

  return (params) => {
    const colId = params.column.colId.replace(/^[0-9]{4}-/g, '');
    const colDef = Object.assign({}, {
      // Defaults to prevent undefined issues
      type: '',
      Precision: '',
      Suffix: '',
    }, defs[colId]);

    let value = params.value.text;
    const precision = getPrecision(colDef);

    if (appDefault.dataNotAvailable.indexOf(value) > -1 ||
      params.column.colId === 'Location') {
      return escapeNewLines(
        attachFootnotes(escapeCSVQuotes(value), params)
      );
    }

    if (colDef.type === 'percent') {
      // 2 Additional places are added to account for the decimal
      // place difference between raw and formatted percent values.
      const percentPrecision = precision === 0 && value < 0.1 ?
        2 : precision + 2;
      if (!kffIsNumber(value)) {
        return escapeNewLines(
          attachFootnotes(escapeCSVQuotes(value), params)
        );
      }
      value = numberPrecision(value, percentPrecision, false)
        .toFixed(percentPrecision);
    } else if (colDef.Precision) {
      value = numberPrecision(
        value,
        getPrecision(colDef),
        false
      );
    }

    if (colDef.type === 'currency' && !isNaN(value)) {
      return escapeNewLines(
        attachFootnotes(
          `$${escapeCSVQuotes(
            numberPrecision(value, precision, false).toFixed(precision)
          )}`, params
        )
      );
    }

    if (colDef.Suffix) {
      value = value + colDef.Suffix;
    }

    return escapeNewLines(attachFootnotes(escapeCSVQuotes(value), params));
  };
}

// export function processHeaderCallback() {
//   return ({ column }) => colId)}
//   //   return params.column.colId
//   //     .replace('__', '\n')
//   //     .replace(/^([0-9]{4})-(.*)/, '$1\n$2');
//   // };
// }

/**
 * Attach footnotes to each applicable cell... yay!
 * @param  {string} value  cell value
 * @param  {object} params ag grid params object
 * @return {string}        string with footnotes attached (if applicable)
 */
function attachFootnotes(value, params) {
  if (params.api.footnoteCallback) {
    const { data, group, child } = params.value;
    const footnotes = params.api.footnoteCallback([
      data.Location || data.State, // State for geomulti,
      (child !== false) ?
        ([group, child]) :
        group,
    ]).map(({ label }) => label);
    if (footnotes.length > 0) {
      return `${value}#!#${footnotes.join(',')}#!#`;
    }
    return value;
  }
}

/**
 * Clean up and modify the CSV as needed
 * @param  {string} csv csv string
 * @return {[type]}     [description]
 */
export function cleanCSV(csv) {
  // Split csv on new line
  // Iterate through each line
  // check line for existance of `#!#`
  // if has existance, split on `#!#`
  // take split into two array
  // dedupe second, prepend to end of first with a `,`
  // join('')
  return csv.split('\n')
    .map((line, idx) => {
      // headers are the 3rd row
      if (idx === 2) {
        return `${
          line.replace(/^([0-9]{4})-(.*)/g, '$1\n$2')
            .slice(0, -1) // remove `\n` at end of line
        },Footnotes`;
      }
      if (line.match(/#!#/g) !== null) {
        const [data, notes] = splitArray(line.split('#!#'));
        const footnotes = notes
          .reduce((prev, note) => prev.concat(note.split(',')), [])
          .filter((note, key, self) => self.indexOf(note) === key) // dedup
          .sort((a, b) => a - b) // sort
          .join(', ');
        return `${data.join('').slice(0, -1)},"${footnotes}"`;
      }

      return line;
    })
    .join('\n');
}

/**
 * Creator and downloader for the csv data (taken from AG-grids implimentation)
 * @param  {string} fileName [filename]
 * @param  {string} content  [content for file (csv, json, etc)]
 */
export function downloader(fileName, content) {
  // We need to use a form submission so browsers will properly download the file...
  const form = document.createElement('form');
  form.setAttribute('id', 'raw-data-form');
  form.setAttribute('action', 'http://util.kff.org:8081');
  // form.setAttribute('action', 'http://localhost:8081'); // For local debugging
  form.setAttribute('method', 'post');
  form.appendChild(buildInput('file-name', fileName));
  form.appendChild(buildInput('content-type', 'text/csv'));
  form.appendChild(buildInput('data', content));
  const submit = document.createElement('button');
  submit.setAttribute('type', 'submit');
  form.appendChild(submit);

  document.body.appendChild(form);
  submit.click();
  document.body.removeChild(form);

  function buildInput(name, value) {
    const input = document.createElement('input');
    input.setAttribute('type', 'hidden');
    input.setAttribute('name', name);
    input.setAttribute('value', value);
    return input;
  }
}

/**
 * Escape CSV quotes
 * @param  {string} str [string to escape]
 * @return {string}     [escaped string]
 */
export function escapeCSVQuotes(str) {
  if (typeof str !== 'string') {
    return str;
  }
  return str.replace(/"|“|”|\u201C|\u201D/g, '""');
}

/**
 * Check string for newlines, if they exist wrap in quotes
 * @param  {string} str [string to check]
 * @return {string}     [string wrapped in quotes]
 */
export function escapeNewLines(str) {
  if (typeof str !== 'string') {
    return str;
  }
  if (str.match('\n|\r')) {
    return str.replace(/(\r\n|\n|\r)/gm, ' ');
  }
  return str;
}

/**
 * Convience function for splitting an array every other item
 * @param  {array} arr array to split
 * @return {array}     2d array, first one is the evens (including 0), second is odds (not including 0)
 */
function splitArray(arr) {
  return [
    arr.filter((item, idx) => idx % 2 === 0),
    arr.filter((item, idx) => idx % 2 !== 0),
  ];
}



// WEBPACK FOOTER //
// ../datacenter/app/js/services/processTableDataForDownload.js